import React, { useEffect, useState } from "react";
import axios from "axios";
import CircularProgress from "@material-ui/core/CircularProgress";

const listeners = new Set();
let pendingCount = 0;
let installed = false;
let nextRequestId = 1;
const pendingRequests = new Map();

const emit = () => {
  const latestRequest = Array.from(pendingRequests.values()).slice(-1)[0];
  listeners.forEach((listener) => listener({
    count: pendingCount,
    message: latestRequest?.message || "Loading data...",
  }));
};

const getLoaderMessage = (config = {}) => {
  if (config.loadingMessage) return config.loadingMessage;

  const method = String(config.method || "get").toUpperCase();
  const url = String(config.url || "").toLowerCase();

  if (url.includes("/signin")) return "Signing in...";
  if (url.includes("customer-due") || url.includes("get-customer-due")) return "Loading customer due report...";
  if (url.includes("supplier-due")) return "Loading supplier due report...";
  if (url.includes("ledger")) return "Loading ledger report...";
  if (url.includes("stock") || url.includes("product-location")) return "Loading stock data...";
  if (url.includes("product-search") || url.includes("get-products")) return "Searching products...";
  if (url.includes("invoice")) return "Loading invoice data...";
  if (url.includes("report") || url.includes("record") || url.includes("list")) return "Loading report...";
  if (url.includes("save") || url.includes("add") || url.includes("create")) return "Saving data...";
  if (url.includes("update") || url.includes("edit")) return "Updating data...";
  if (url.includes("delete") || url.includes("remove")) return "Deleting data...";
  if (method !== "GET") return "Processing request...";

  return "Loading data...";
};

const shouldTrackConfig = (config = {}) => {
  return config.showNetworkLoader !== false;
};

const increment = (message = "Loading data...") => {
  pendingCount += 1;
  const requestId = nextRequestId++;
  const timeoutId = setTimeout(() => {
    decrement(requestId);
  }, 45000);
  pendingRequests.set(requestId, { message, timeoutId });
  emit();
  return requestId;
};

const decrement = (requestId) => {
  if (!requestId) return;
  const request = pendingRequests.get(requestId);
  if (!request) return;
  if (request?.timeoutId) clearTimeout(request.timeoutId);
  pendingCount = Math.max(0, pendingCount - 1);
  pendingRequests.delete(requestId);
  emit();
};

export const installAxiosNetworkLoader = (instance = axios) => {
  if (installed) return;
  installed = true;

  instance.interceptors.request.use(
    (config) => {
      if (shouldTrackConfig(config)) {
        config.__networkLoaderRequestId = increment(getLoaderMessage(config));
      }
      return config;
    },
    (error) => {
      decrement(error?.config?.__networkLoaderRequestId);
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      decrement(response?.config?.__networkLoaderRequestId);
      return response;
    },
    (error) => {
      decrement(error?.config?.__networkLoaderRequestId);
      // Prevent uncaught errors by ensuring we always return a rejected promise
      // This allows parent components to handle the error properly
      if (error?.response?.status >= 500) {
        console.error("Server error:", {
          status: error.response.status,
          message: error?.response?.data?.message || error.message
        });
      } else if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
        console.warn("Network connectivity issue:", error.message);
      }
      return Promise.reject(error);
    }
  );

  if (typeof window !== "undefined" && typeof window.fetch === "function") {
    const originalFetch = window.fetch.bind(window);
    window.fetch = (...args) => {
      const init = args[1] || {};
      if (init?.showNetworkLoader === false) {
        return originalFetch(...args);
      }

      const requestId = increment("Loading data...");
      return originalFetch(...args)
        .then((response) => {
          decrement(requestId);
          return response;
        })
        .catch((error) => {
          decrement(requestId);
          throw error;
        });
    };
  }
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 99999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "0",
  padding: "24px",
  background:
    "radial-gradient(circle at center, rgba(255,255,255,0.72) 0%, rgba(244,248,247,0.84) 45%, rgba(230,237,236,0.94) 100%)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
};

const cardStyle = {
  width: "min(380px, calc(100vw - 40px))",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "12px",
  padding: "26px 28px 24px",
  borderRadius: "22px",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.995) 0%, rgba(249,252,252,0.985) 100%)",
  boxShadow:
    "0 22px 60px rgba(14, 72, 69, 0.10), 0 3px 10px rgba(15, 126, 119, 0.05)",
  border: "1px solid rgba(15, 126, 119, 0.09)",
  position: "relative",
  overflow: "hidden",
};

const accentStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "3px",
  background: "linear-gradient(90deg, #0F7E77 0%, #7BC0BA 50%, #0F7E77 100%)",
};

const spinnerWrapStyle = {
  width: 68,
  height: 68,
  display: "grid",
  placeItems: "center",
  borderRadius: "50%",
  background:
    "radial-gradient(circle, rgba(15,126,119,0.14) 0%, rgba(15,126,119,0.04) 58%, rgba(255,255,255,0) 100%)",
  boxShadow: "inset 0 0 0 1px rgba(15, 126, 119, 0.06)",
};

const titleStyle = {
  margin: 0,
  fontSize: "15px",
  fontWeight: 800,
  letterSpacing: "0.01em",
  color: "#0F7E77",
};

const subtitleStyle = {
  margin: 0,
  fontSize: "12px",
  lineHeight: 1.45,
  color: "#6a7675",
  textAlign: "center",
  maxWidth: "31ch",
};

const metaRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  marginTop: "2px",
};

const metaDotStyle = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: "#0F7E77",
  opacity: 0.8,
  animation: "nlBeat 1.4s infinite ease-in-out",
};

const metaTextStyle = {
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "#6f7f7e",
};

export function NetworkLoaderOverlay({ message, visible, delay = 350, fullscreen = true }) {
  const [loaderState, setLoaderState] = useState({
    count: pendingCount,
    message: message || "Loading data...",
  });
  const [showDelayed, setShowDelayed] = useState(false);

  useEffect(() => {
    const listener = (nextState) => setLoaderState(nextState);
    listeners.add(listener);
    setLoaderState({
      count: pendingCount,
      message: message || Array.from(pendingRequests.values()).slice(-1)[0]?.message || "Loading data...",
    });

    return () => {
      listeners.delete(listener);
    };
  }, [message]);

  const busy = typeof visible === "boolean" ? visible : loaderState.count > 0;
  useEffect(() => {
    if (!busy) {
      setShowDelayed(false);
      return undefined;
    }

    const timer = setTimeout(() => setShowDelayed(true), delay);
    return () => clearTimeout(timer);
  }, [busy, delay]);

  const shouldShow = typeof visible === "boolean" ? visible : showDelayed;
  if (!shouldShow) return null;

  const displayMessage = message || loaderState.message || "Loading data...";
  const resolvedOverlayStyle = fullscreen
    ? overlayStyle
    : {
      position: "fixed",
      right: 18,
      bottom: 18,
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    };
  const resolvedCardStyle = fullscreen
    ? cardStyle
    : {
      ...cardStyle,
      width: "min(260px, calc(100vw - 36px))",
      minHeight: "auto",
      flexDirection: "row",
      justifyContent: "flex-start",
      gap: "10px",
      padding: "12px 14px",
      borderRadius: "10px",
      boxShadow: "0 10px 28px rgba(14, 72, 69, 0.14)",
      pointerEvents: "none",
    };
  const resolvedSpinnerWrapStyle = fullscreen
    ? spinnerWrapStyle
    : {
      ...spinnerWrapStyle,
      width: 34,
      height: 34,
      flex: "0 0 auto",
    };
  const resolvedTitleStyle = fullscreen
    ? titleStyle
    : {
      ...titleStyle,
      fontSize: "12px",
      lineHeight: 1.25,
      textAlign: "left",
    };

  return (
    <div style={resolvedOverlayStyle} aria-live="polite" aria-busy="true">
      <div style={resolvedCardStyle}>
        <div style={accentStyle} />
        <div style={resolvedSpinnerWrapStyle}>
          <CircularProgress size={fullscreen ? 38 : 20} thickness={4.5} style={{ color: "#0F7E77" }} />
        </div>
        <div>
          <p style={resolvedTitleStyle}>{displayMessage}</p>
          {fullscreen ? (
            <>
              <p style={subtitleStyle}>Fetching the latest data and preparing the results.</p>
              <div style={metaRowStyle}>
                <span style={{ ...metaDotStyle, animationDelay: "0s" }} />
                <span style={{ ...metaDotStyle, animationDelay: "0.18s" }} />
                <span style={{ ...metaDotStyle, animationDelay: "0.36s" }} />
                <span style={metaTextStyle}>Syncing</span>
              </div>
            </>
          ) : null}
        </div>
      </div>
      <style>{`
        @keyframes nlPulse {
          0% { transform: scale(0.92); opacity: 0.58; box-shadow: 0 0 0 0 rgba(15, 126, 119, 0.18); }
          70% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 10px rgba(15, 126, 119, 0); }
          100% { transform: scale(0.92); opacity: 0.58; box-shadow: 0 0 0 0 rgba(15, 126, 119, 0); }
        }

        @keyframes nlBeat {
          0%, 100% { transform: translateY(0); opacity: 0.55; }
          50% { transform: translateY(-2px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
